import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { catchError } from 'rxjs/operators';
import { UserManager, UserManagerSettings, User, WebStorageStateStore } from 'oidc-client';
import { BehaviorSubject } from 'rxjs';

import { BaseService } from '../../shared/base.service';
import { ConfigService } from '../../shared/config.service';
import { NavStateService } from '../../shell/nav-state.service';

@Injectable({
  providedIn: 'root'
})
export class AuthService extends BaseService {

  private static readonly LOGIN_IN_PROGRESS_KEY = 'oidc_login_in_progress';
  private static readonly LOGIN_IN_PROGRESS_AT_KEY = 'oidc_login_in_progress_at';
  private static readonly LOGIN_IN_PROGRESS_MAX_AGE_MS = 180000;
  private static readonly OIDC_STATE_STORAGE_PREFIXES = ['oidc.', 'oidc_'];
  private static readonly OIDC_USER_STORAGE_PREFIX = 'oidc.user:';

  // Observable navItem source
  private _authNavStatusSource = new BehaviorSubject<boolean>(false);
  // Observable navItem stream
  authNavStatus$ = this._authNavStatusSource.asObservable();

  private manager: UserManager | null;
  private user: User | null;
  private completeAuthenticationPromise: Promise<void> | null = null;
  private authEventsRegistered = false;
  navbar: any;
  status = 0;
  showlogin = true;

  constructor(
    private http: HttpClient,
    private configService: ConfigService,
    private navStateService: NavStateService
  ) {
    super();
    this.manager = new UserManager(getClientSettings(configService));
  }
  // se agrega el método init para inicializar el estado de autenticación al iniciar la aplicación
  async init(): Promise<void> {
    try {
      if (this.isPostLogoutRedirect()) {
        await this.finalizePostLogoutRedirect();
        return;
      }

      // Defensive cleanup: if a previous attempt left redirect flags pointing
      // to the app root or a stale login_in_progress marker, clear them so we
      // don't get stuck on an empty route.
      try {
        const redirectedFlag = sessionStorage.getItem('redirected_from_protected');
        const redirectedUrl = sessionStorage.getItem('redirected_from_protected_url');
        if (redirectedFlag === '1' && (!redirectedUrl || redirectedUrl === '/')) {
          sessionStorage.removeItem('redirected_from_protected');
          sessionStorage.removeItem('redirected_from_protected_url');
        }
      } catch (e) {
        // ignore
      }

      try {
        const inProgress = sessionStorage.getItem(AuthService.LOGIN_IN_PROGRESS_KEY) === '1';
        const startedAt = Number(sessionStorage.getItem(AuthService.LOGIN_IN_PROGRESS_AT_KEY) || '0');
        const now = Date.now();
        if (inProgress && !this.isAuthCallbackRoute()) {
          this.clearLoginProgress();
        }
      } catch (e) {
        // ignore
      }

      // El callback de login se consume exclusivamente en la ruta /auth-callback.
      // APP_INITIALIZER no debe intentar procesarlo automáticamente.
      // If the Identity Provider redirected back to the app root (or any
      // non '/auth-callback' path) but included OIDC parameters (code/state or error),
      // move the browser to '/auth-callback' so the callback is consumed.
      const currentUrl = new URL(window.location.href);
      const hasOidcSearchParams = currentUrl.searchParams.has('code') || currentUrl.searchParams.has('state') || currentUrl.searchParams.has('error');
      const hash = window.location.hash || '';
      const hasOidcHashParams = hash.indexOf('code=') !== -1 || hash.indexOf('state=') !== -1 || hash.indexOf('error=') !== -1;
      if ((hasOidcSearchParams || hasOidcHashParams) && !this.isAuthCallbackRoute(currentUrl.pathname)) {
        // Move to /auth-callback and preserve search and hash so the
        // callback component/oidc-client can consume them.
        currentUrl.pathname = '/auth-callback';
        window.location.replace(currentUrl.toString());
        return;
      }

      if (this.isAuthCallbackRoute()) {
        return;
      }

      this.user = await this.manager.getUser();
      this._authNavStatusSource.next(this.isAuthenticated());
      this.registerAuthEvents();
    } catch (e) {
      this.user = null;
      this._authNavStatusSource.next(false);
    }
  }

  isAuthCallbackRoute(url: string = window.location.pathname): boolean {
    return url.includes('/auth-callback');
  }

  isAuthenticationCallbackInProgress(): boolean {
    return this.completeAuthenticationPromise != null;
  }

  private isPostLogoutRedirect(): boolean {
    const currentUrl = new URL(window.location.href);
    return currentUrl.searchParams.get('postLogout') === '1';
  }

  private async finalizePostLogoutRedirect(): Promise<void> {
    try {
      await this.manager.signoutRedirectCallback();
    } catch (error) {
      console.warn('[AUTH-SERVICE] No se pudo completar signoutRedirectCallback:', error);
    }

    await this.clearClientData();

    this.user = null;
    this._authNavStatusSource.next(false);

    const currentUrl = new URL(window.location.href);
    currentUrl.searchParams.delete('postLogout');
    currentUrl.pathname = '/';
    window.location.replace(currentUrl.toString());
  }

  /* login() {
    if (this.isAuthCallbackRoute() || this.isAuthenticationCallbackInProgress()) {
      return Promise.resolve();
    }

    const inProgress = sessionStorage.getItem(AuthService.LOGIN_IN_PROGRESS_KEY) === '1';
    const startedAt = Number(sessionStorage.getItem(AuthService.LOGIN_IN_PROGRESS_AT_KEY) || '0');
    const now = Date.now();

    if (inProgress && (now - startedAt) < AuthService.LOGIN_IN_PROGRESS_MAX_AGE_MS) {
      return Promise.resolve();
    }

    return this.startLogin(now);
  } */

    login() {
    return this.manager.signinRedirect();
  }

  private async startLogin(now: number): Promise<void> {
    await this.clearStaleOidcData(true);

    sessionStorage.setItem(AuthService.LOGIN_IN_PROGRESS_KEY, '1');
    sessionStorage.setItem(AuthService.LOGIN_IN_PROGRESS_AT_KEY, now.toString());

    try {
      await this.manager.signinRedirect();
    } catch (error) {
      this.clearLoginProgress();
      throw error;
    }
  }

  async completeAuthentication() {
    if (this.completeAuthenticationPromise) {
      return this.completeAuthenticationPromise;
    }

    this.completeAuthenticationPromise = this.runCompleteAuthentication();

    try {
      await this.completeAuthenticationPromise;
    } finally {
      this.completeAuthenticationPromise = null;
    }
  }

  private async runCompleteAuthentication(): Promise<void> {
    try {
      // Punto único de consumo del callback OIDC de login.
      this.user = await this.manager.signinRedirectCallback();
      await this.clearStaleOidcData(false);
      this._authNavStatusSource.next(this.isAuthenticated());
      this.registerAuthEvents();
    } catch (error) {
      console.error('[AUTH-SERVICE] Error en signinRedirectCallback:', error);
      console.error('[AUTH-SERVICE] Storage OIDC keys:', this.getOidcStorageKeys());
      await this.clearStaleOidcData(true);
      throw error;
    } finally {
      this.clearLoginProgress();
    }
  }

  private registerAuthEvents(): void {
    if (this.authEventsRegistered || !this.manager) {
      return;
    }

    this.manager.events.addUserLoaded((user) => {
      this.user = user;
      console.log(this.user.profile.role);
      this._authNavStatusSource.next(this.isAuthenticated());
    });

    this.manager.events.addUserUnloaded(() => {
      this.user = null;
      this._authNavStatusSource.next(false);
    });

    this.manager.events.addUserSignedOut(() => {
      this.user = null;
      this._authNavStatusSource.next(false);
    });

    this.authEventsRegistered = true;
  }

  register(userRegistration: any) {
    return this.http.post(this.configService.authApiURI + '/account', userRegistration).pipe(catchError(this.handleError));
  }

  isAuthenticated(): boolean {
    return this.user != null && !this.user.expired;
  }

  get authorizationHeaderValue(): string {
    return `${this.user.token_type} ${this.user.access_token}`;
  }

  get partnerStatus(): any {
    return this.status;
  }

  get name(): string {
    let aux = (this.user != null && this.user.profile != null && this.user.profile.name != null) ? this.user.profile.name : '';
    if (aux == 'pindi_de_ejemplo') {
      return 'PINDI_TECA';
    } else {
      return aux;
    }
  }

  get roles(): any {
    if (this.user == null || this.user.profile == null || this.user.profile.role == null) {
      return [];
    }
    const role = this.user.profile.role;
    if (Array.isArray(role)) {
      return role;
    }
    if (typeof role === 'string') {
      // Try parse JSON array string
      try {
        const parsed = JSON.parse(role);
        if (Array.isArray(parsed)) {
          return parsed;
        }
      } catch {
        // ignore
      }
      // Fallback: comma-separated string
      return role.split(',').map((r: string) => r.trim()).filter((r: string) => r.length > 0);
    }
    return [];
  }

  get sub(): any {
    return (this.user != null && this.user.profile != null && this.user.profile.sub != null) ? this.user.profile.sub : ''
  }
  get isAdmin(): boolean {
    return (this.user != null && this.user.profile != null && this.user.profile.is_admin === 'True') ? true : false;
  }

  get idpartner(): string {
    return (this.user != null && this.user.profile != null && this.user.profile.idpartner != null) ? this.user.profile.idpartner : '';
  }

  get password_expiry_days_remaining(): string {
    return (this.user != null && this.user.profile != null && this.user.profile.password_expiry_days_remaining != null) ? this.user.profile.password_expiry_days_remaining : '99';
  }

  get EmployeeId(): string {
    return (this.user != null && this.user.profile != null && this.user.profile.EmployeeId != null) ? this.user.profile.idpartner : 'MPA-Unknown';
  }

  get canal(): string {
    if (this.user != null && this.user.profile != null && this.user.profile.is_admin === 'True') {
      return 'Admin';
    } else {
      return (this.user != null && this.user.profile != null && this.user.profile.canal != null) ? this.user.profile.canal : '';
    }
  }

  get center(): string {
    if (this.user != null && this.user.profile != null && this.user.profile.is_admin === 'True' &&
      (this.user.profile == null || this.user.profile.center == null)) {
      return 'Admin';
    } else {
      return (this.user != null && this.user.profile != null && this.user.profile.center != null) ? this.user.profile.center : '';
    }
  }

  /**
   * Limpia la sesión cuando expira desde el servidor (401).
   * No hace redirect al proveedor ya que la sesión ya ha expirado allá.
   */
  async clearSessionOnExpiry(): Promise<void> {
    // Limpiar datos locales
    this.user = null;
    this.clearLoginProgress();
    await this.clearStaleOidcData(true);

    // Emitir que no está autenticado
    this._authNavStatusSource.next(false);

    // Limpiar navbar
    this.navStateService.clearNavbar();
  }

  async signout() {
    // Emitir inmediatamente que no está autenticado para limpiar la UI
    this._authNavStatusSource.next(false);
    this.user = null;
    this.clearLoginProgress();

    await this.clearStaleOidcData(false);

    try {
      await this.manager.signoutRedirect();
    } catch (error) {
      console.warn('[AUTH-SERVICE] No se pudo iniciar signoutRedirect:', error);
      await this.clearClientData();
      window.location.replace(this.configService.spaURI);
    }
  }

  private async clearClientData(): Promise<void> {
    this.navStateService.clearNavbar();
    this.clearLoginProgress();
    await this.clearStaleOidcData(true);

    try {
      await this.manager.removeUser();
    } catch (e) {
      console.warn('No se pudo eliminar el usuario OIDC en logout:', e);
    }

    this.removeOidcStorageArtifacts(localStorage, true);
    this.removeOidcStorageArtifacts(sessionStorage, true);

    try {
      const cacheStorage = (window as any).caches;
      if (cacheStorage && typeof cacheStorage.keys === 'function') {
        const cacheNames = await cacheStorage.keys();
        await Promise.all(cacheNames.map((cacheName: string) => cacheStorage.delete(cacheName)));
      }
    } catch (e) {
      console.warn('No se pudo limpiar Cache Storage en logout:', e);
    }

    try {
      const idbFactory = (window as any).indexedDB;
      if (idbFactory && typeof idbFactory.databases === 'function') {
        const dbs = await idbFactory.databases();
        for (const db of dbs) {
          if (db && db.name) {
            idbFactory.deleteDatabase(db.name);
          }
        }
      }
    } catch (e) {
      console.warn('No se pudo limpiar IndexedDB en logout:', e);
    }
  }

  private async clearStaleOidcData(removeUserStore: boolean): Promise<void> {
    try {
      await this.manager.clearStaleState();
    } catch (error) {
      console.warn('[AUTH-SERVICE] No se pudo limpiar estado OIDC obsoleto:', error);
    }

    this.removeOidcStorageArtifacts(localStorage, removeUserStore);
    this.removeOidcStorageArtifacts(sessionStorage, removeUserStore);
  }

  public clearLoginProgress(): void {
    sessionStorage.removeItem(AuthService.LOGIN_IN_PROGRESS_KEY);
    sessionStorage.removeItem(AuthService.LOGIN_IN_PROGRESS_AT_KEY);
  }

  private removeOidcStorageArtifacts(storage: Storage, removeUserStore: boolean): void {
    const keysToRemove: string[] = [];

    for (let index = 0; index < storage.length; index++) {
      const key = storage.key(index);
      if (key && this.isOidcStorageKey(key, removeUserStore)) {
        keysToRemove.push(key);
      }
    }

    for (const key of keysToRemove) {
      storage.removeItem(key);
    }
  }

  private isOidcStorageKey(key: string, removeUserStore: boolean): boolean {
    const normalizedKey = key.toLowerCase();
    return AuthService.OIDC_STATE_STORAGE_PREFIXES.some(prefix => normalizedKey.startsWith(prefix))
      || (removeUserStore && normalizedKey.startsWith(AuthService.OIDC_USER_STORAGE_PREFIX));
  }

  private getOidcStorageKeys(): string[] {
    return [localStorage, sessionStorage]
      .reduce((keys: string[], storage) => {
        for (let index = 0; index < storage.length; index++) {
          const key = storage.key(index);
          if (key && this.isOidcStorageKey(key, true)) {
            keys.push(key);
          }
        }
        return keys;
      }, []);
  }

  /**
   * Devuelve true si el usuario fue redirigido al login desde una ruta protegida.
   * Guarda la ruta en sessionStorage desde el AuthGuard.
   */
  wasRedirectedFromProtectedRoute(): boolean {
    return sessionStorage.getItem('redirected_from_protected') === '1';
  }

  /**
   * Mark that the user was redirected to login from a protected route.
   * Optionally store the original URL so we can restore it after login.
   */
  setRedirectedFromProtectedRoute(value: boolean, originalUrl?: string): void {
    if (value) {
      sessionStorage.setItem('redirected_from_protected', '1');
      if (originalUrl) {
        sessionStorage.setItem('redirected_from_protected_url', originalUrl);
      }
    } else {
      sessionStorage.removeItem('redirected_from_protected');
      sessionStorage.removeItem('redirected_from_protected_url');
    }
  }

  getRedirectedFromProtectedUrl(): string | null {
    const val = sessionStorage.getItem('redirected_from_protected_url');
    if (!val) { return null; }
    // Treat root or empty as no meaningful target
    if (val === '/' || val === '') { return null; }
    return val;
  }
}

export function getClientSettings(configService: ConfigService): UserManagerSettings {
  return {
    authority: configService.authApiURI,
    client_id: 'angular_spa',
    redirect_uri: configService.spaURI + '/auth-callback',
    post_logout_redirect_uri: configService.spaURI + '/?postLogout=1',
    response_type: 'code',
    scope: 'openid profile web_api offline_access',
    filterProtocolClaims: true,
    loadUserInfo: true,
    automaticSilentRenew: true,
    silent_redirect_uri: configService.spaURI + '/silent-refresh.html',
    revokeAccessTokenOnSignout: true,
    userStore: new WebStorageStateStore({ store: window.localStorage }),
    stateStore: new WebStorageStateStore({ store: window.localStorage }),
  };
}
